/**
 * 8D Module API - Steps
 *
 * Database operations for 8D process steps (D1-D8).
 * Uses mod_8d schema for module isolation.
 */

import { supabase } from '@/integrations/supabase/client';

// =============================================================================
// Types
// =============================================================================

export interface Step {
  id: string;
  tenant_id: string;
  claim_id: string;
  step_number: number;
  title: string;
  content: string | null;
  status: string;
  completed_at: string | null;
  completed_by: string | null;
  rejected_at: string | null;
  rejected_by: string | null;
  rejection_note: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateStepInput {
  tenant_id: string;
  claim_id: string;
  step_number: number;
  title: string;
}

export interface UpdateStepContentInput {
  content: string;
  status: string;
  completed_at?: string | null;
  completed_by?: string | null;
}

export interface RejectStepInput {
  status: string;
  rejected_at: string;
  rejected_by: string;
  rejection_note: string;
  completed_at: null;
  completed_by: null;
}

// Standard 8D step titles
export const STEP_TITLES = [
  'D1: Team Formation',
  'D2: Problem Description',
  'D3: Containment Actions',
  'D4: Root Cause Analysis',
  'D5: Corrective Actions',
  'D6: Verification',
  'D7: Prevent Recurrence',
  'D8: Team Recognition',
];

// =============================================================================
// Queries
// =============================================================================

/**
 * Fetches all steps for a claim ordered by step number.
 */
export async function fetchStepsByClaim(claimId: string): Promise<Step[]> {
  const { data, error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .select('*')
    .eq('claim_id', claimId)
    .order('step_number');

  if (error) throw error;
  return data;
}

/**
 * Creates the 8 standard steps for a new claim.
 */
export async function createInitialSteps(tenantId: string, claimId: string): Promise<Step[]> {
  const stepsToInsert = STEP_TITLES.map((title, index) => ({
    tenant_id: tenantId,
    claim_id: claimId,
    step_number: index + 1,
    title,
  }));

  const { data, error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .insert(stepsToInsert)
    .select();

  if (error) throw error;
  return data;
}

/**
 * Updates step content and status (for supplier submitting work).
 */
export async function updateStepContent(
  stepId: string,
  updates: UpdateStepContentInput
): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .update({
      content: updates.content,
      status: updates.status,
      completed_at: updates.completed_at !== undefined ? updates.completed_at : (updates.status === 'completed' ? new Date().toISOString() : null),
      completed_by: updates.completed_by || null,
    })
    .eq('id', stepId);

  if (error) throw error;
}

/**
 * Updates step content by claim ID and step number.
 */
export async function updateStepByNumber(
  claimId: string,
  stepNumber: number,
  updates: UpdateStepContentInput
): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .update({
      content: updates.content,
      status: updates.status,
      completed_at: updates.status === 'completed' ? new Date().toISOString() : null,
      completed_by: updates.completed_by || null,
    })
    .eq('claim_id', claimId)
    .eq('step_number', stepNumber);

  if (error) throw error;
}

/**
 * Approves a step (factory approving supplier's work).
 */
export async function approveStep(stepId: string, approvedBy: string): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .update({
      status: 'approved',
      completed_at: new Date().toISOString(),
      completed_by: approvedBy,
    })
    .eq('id', stepId);

  if (error) throw error;
}

/**
 * Rejects a step (factory rejecting supplier's work).
 */
export async function rejectStep(stepId: string, input: RejectStepInput): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .update({
      status: input.status,
      rejected_at: input.rejected_at,
      rejected_by: input.rejected_by,
      rejection_note: input.rejection_note,
      completed_at: null,
      completed_by: null,
    })
    .eq('id', stepId);

  if (error) throw error;
}

/**
 * Rejects a step by claim ID and step number.
 */
export async function rejectStepByNumber(
  claimId: string,
  stepNumber: number,
  rejectedBy: string,
  rejectionNote: string
): Promise<void> {
  const { error } = await supabase
    .schema('mod_8d')
    .from('steps')
    .update({
      status: 'in_progress',
      rejected_at: new Date().toISOString(),
      rejected_by: rejectedBy,
      rejection_note: rejectionNote,
      completed_at: null,
      completed_by: null,
    })
    .eq('claim_id', claimId)
    .eq('step_number', stepNumber);

  if (error) throw error;
}
